import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Crown, Check, Loader2, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';
import { myiaTexts } from '../../utils/myiaTranslations';

/**
 * v50.14 — Subscription refactor
 * Three exports, all wired to the same admin master toggle:
 *   - SubscriptionButton: a CTA button (rendered below "Green IA" in MyIA).
 *     Hidden when the admin master toggle is OFF.
 *   - SubscriptionModal: full-screen modal opened by the button.
 *   - default SubscriptionPanel: inline card (kept for backwards compat but
 *     unused in FilmIA.jsx after v50.14).
 */

function usePremiumSettings() {
  const { language } = useLanguage();
  const [state, setState] = useState(null);
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState(true);

  const refetch = React.useCallback(() => {
    Promise.all([
      axios.get('/api/subscription/settings', { params: { lang: language } }).then(r => r.data).catch(() => null),
      axios.get('/api/subscription/mine').then(r => r.data).catch(() => ({})),
    ]).then(([s, m]) => { setState(s); setMine(m); setLoading(false); });
  }, [language]);

  useEffect(() => {
    refetch();
    // Refresh when the tab regains focus (catches admin toggle changes)
    const onFocus = () => refetch();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refetch]);

  return { state, mine, loading, refetch, refreshMine: () => axios.get('/api/subscription/mine').then(r => setMine(r.data)) };
}

/** v50.16 — Purchased dubbing credits (100 per credits_20 pack). */
function CreditsBalance() {
  const { language } = useLanguage();
  const T = myiaTexts(language);
  const [credits, setCredits] = useState(null);
  useEffect(() => {
    axios.get('/api/subscription/credits/mine').then(r => setCredits(r.data.credits)).catch(() => {});
  }, []);
  if (credits === null) return null;
  return (
    <div className="px-5 pt-3 text-xs text-yellow-200 font-semibold" data-testid="sub-credits-balance">
      {(T.subCreditsBalance || 'Mes crédits : {n}').replace('{n}', String(credits))}
    </div>
  );
}

/** Button that opens the subscription modal — hidden if the admin master
 * toggle is OFF (matches the "par défaut pas d'abonnement" default). */
export function SubscriptionButton({ onOpen }) {
  const { language } = useLanguage();
  const T = myiaTexts(language);
  const { state, mine, loading } = usePremiumSettings();
  if (loading) return null;
  if (!state?.enabled) return null;
  const active = !!mine?.active;
  return (
    <button
      onClick={onOpen}
      className="mt-3 w-full p-5 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-500/40 rounded-2xl hover:from-yellow-500/30 hover:to-amber-500/30 transition-all text-left group flex items-center gap-4"
      data-testid="open-subscription"
    >
      <div className="p-3 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-xl group-hover:scale-110 transition-transform">
        <Crown className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1">
        <h4 className="font-bold text-yellow-200 flex items-center gap-2">
          {active ? T.subActive : T.subOfferTitle}
          {active && <Sparkles className="w-3.5 h-3.5" />}
        </h4>
        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{T.subOfferSub}</p>
      </div>
    </button>
  );
}

/** Full-screen modal with the actual subscription UI (offer or active state). */
export function SubscriptionModal({ onClose }) {
  const { language } = useLanguage();
  const T = myiaTexts(language);
  const { state, mine, loading, refreshMine } = usePremiumSettings();
  const [busy, setBusy] = useState(false);

  // Master admin toggle: if subscriptions are disabled AND user has no active
  // subscription, don't render anything (auto-close). Users who already
  // subscribed keep access to their existing subscription management screen.
  if (!loading && !state?.enabled && !mine?.active) {
    if (typeof onClose === 'function') setTimeout(onClose, 0);
    return null;
  }

  const checkout = async (plan) => {
    setBusy(true);
    try {
      const r = await axios.post('/api/subscription/checkout', { plan, lang: language });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || T.subCheckoutKo);
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm(T.subCancelConfirm)) return;
    try {
      await axios.post('/api/subscription/cancel/mine');
      await refreshMine();
      toast.success(T.subCancelled);
    } catch { toast.error(T.subCheckoutKo); }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-md overflow-y-auto" data-testid="subscription-modal">
      <div className="min-h-full flex items-start justify-center px-4 py-6">
        <div className="w-full max-w-md bg-gradient-to-br from-yellow-950/95 to-amber-950/95 border border-yellow-500/40 rounded-3xl shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-yellow-500/20 sticky top-0 bg-yellow-950/95 backdrop-blur-md rounded-t-3xl z-10">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-yellow-500 to-amber-600">
                <Crown className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-bold text-yellow-100 text-base">
                {mine?.active ? T.subActive : T.subOfferTitle}
              </h3>
            </div>
            <button onClick={onClose} className="p-2 rounded-full bg-white/10 hover:bg-white/20" data-testid="sub-close">
              <X className="w-5 h-5 text-yellow-100" />
            </button>
          </div>

          {/* v50.16 — dubbing credit balance */}
          <CreditsBalance />

          {loading ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-yellow-300" /></div>
          ) : mine?.active ? (
            <div className="p-5 space-y-3" data-testid="subscription-active">
              {mine.subscription?.is_gift && (
                <div className="flex items-center gap-2 p-2.5 rounded-xl bg-fuchsia-500/20 border border-fuchsia-400/40 text-xs text-fuchsia-100" data-testid="subscription-gift-badge">
                  <span className="text-lg">🎁</span>
                  <span>Cadeau offert par Destine{mine.subscription?.note ? ` — ${mine.subscription.note}` : ''}.</span>
                </div>
              )}
              <p className="text-sm text-yellow-100/90">
                {T.subPlanEnds.replace('{plan}', mine.subscription?.plan || '').replace('{date}', mine.subscription?.ends_at?.slice(0, 10) || '')}
              </p>
              <button onClick={cancel} className="w-full py-3 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-200 font-semibold text-sm" data-testid="subscription-cancel">
                {T.subCancel}
              </button>
            </div>
          ) : state?.enabled ? (
            <div className="p-5 space-y-4" data-testid="subscription-offer">
              <p className="text-sm text-yellow-100/85 leading-relaxed">{T.subOfferSub}</p>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => checkout('monthly')} disabled={busy} className="p-3 rounded-xl bg-black/40 border border-yellow-500/30 hover:border-yellow-400 disabled:opacity-40" data-testid="sub-plan-monthly">
                  <div className="text-base font-bold text-yellow-200">{state.prices.symbol}{state.prices.monthly}</div>
                  <div className="text-[10px] text-yellow-200/60">{T.subMonth}</div>
                </button>
                <button onClick={() => checkout('yearly')} disabled={busy} className="p-3 rounded-xl bg-gradient-to-br from-yellow-500 to-amber-600 hover:brightness-110 disabled:opacity-40 relative" data-testid="sub-plan-yearly">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-full bg-black/70 text-[9px] text-yellow-300 font-bold whitespace-nowrap">-17%</div>
                  <div className="text-base font-bold text-black">{state.prices.symbol}{state.prices.yearly}</div>
                  <div className="text-[10px] text-black/70">{T.subYear}</div>
                </button>
                <button onClick={() => checkout('credits_20')} disabled={busy} className="p-3 rounded-xl bg-black/40 border border-yellow-500/30 hover:border-yellow-400 disabled:opacity-40" data-testid="sub-plan-credits">
                  <div className="text-base font-bold text-yellow-200">{state.prices.symbol}{state.prices.credits_20}</div>
                  <div className="text-[10px] text-yellow-200/60">{T.subCredits}</div>
                </button>
              </div>
              <div className="space-y-1.5 text-xs text-yellow-100/85">
                {[T.subFeat1, T.subFeat2, T.subFeat3, T.subFeat4, T.subFeat5].map((f, i) => (
                  <div key={i} className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-400 shrink-0" /> {f}</div>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-5 text-sm text-yellow-100/70 text-center">
              {T.subOfferSub}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SubscriptionPanel() {
  // Inline variant — kept for backwards compat if anything imports the default.
  const [open, setOpen] = useState(false);
  return (
    <>
      <SubscriptionButton onOpen={() => setOpen(true)} />
      {open && <SubscriptionModal onClose={() => setOpen(false)} />}
    </>
  );
}
